import { useMemo, useState } from "react";
import {
  Typography,
  DenovoIcon,
  Button,
  Tooltip,
  TooltipTrigger,
  DialogTrigger,
  Popover,
} from "@repo/design-system";
import type { IDataRow } from "@/api/molecularData";
import { COLUMN_DESCRIPTIONS, LEFT_ALIGN_COLS } from "@/lib/columnDescriptions";
import { CellContent, SmilesMolecule } from "./_parts";
import {
  BSAD_ID_COL_KEY,
  MIN_COL_WIDTH_PX,
  MOLECULE_COL_KEY,
  MOLECULE_WIDTH,
  NO_INFO_COLS,
  colWidthPx,
  truncate,
  type IColumnEntry,
} from "./helpers";

interface IMolecularDataTableProps {
  columns: IColumnEntry[];
  rows: IDataRow[];
  selectedIds: string[];
  onRowClick: (row: IDataRow) => void;
}

export function MolecularDataTable({
  columns,
  rows,
  selectedIds,
  onRowClick,
}: IMolecularDataTableProps) {
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const widthMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const col of columns) {
      const hasInfoButton =
        !NO_INFO_COLS.has(col.displayName) &&
        Boolean(COLUMN_DESCRIPTIONS[col.displayName]);
      map[col.key] = colWidthPx(col, rows, hasInfoButton);
    }
    return map;
  }, [columns, rows]);

  if (columns.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-borderPrimary bg-backgroundSecondary py-16">
        <DenovoIcon
          icon="data"
          width={40}
          height={40}
          className="text-contentTertiary"
        />
        <Typography
          variant="paragraph-l-medium"
          className="text-contentSecondary">
          No data available
        </Typography>
      </div>
    );
  }

  // Filter out the BSA-DB ID column — it is displayed inside the Molecule cell
  const visibleColumns = columns.filter((c) => c.key !== BSAD_ID_COL_KEY);

  // Inject virtual Molecule column as the second column (after "No.")
  const extendedColumns: IColumnEntry[] = [
    visibleColumns[0] ?? columns[0],
    { key: MOLECULE_COL_KEY, displayName: "Molecule" },
    ...visibleColumns.slice(1),
  ];

  const gridStyle = {
    gridTemplateColumns: extendedColumns
      .map(({ key }) =>
        key === MOLECULE_COL_KEY
          ? `${MOLECULE_WIDTH + 16}px`
          : `${widthMap[key] || MIN_COL_WIDTH_PX}px`,
      )
      .join(" "),
  };

  // Left offset for sticky Molecule column = width of the first column
  const firstColKey = extendedColumns[0]?.key;
  const moleculeLeftOffset = firstColKey
    ? (widthMap[firstColKey] ?? MIN_COL_WIDTH_PX)
    : 0;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-borderPrimary">
      <div className="min-h-0 flex-1 overflow-auto">
        <div style={gridStyle} className="grid w-max min-w-full">
          {/* ── Header cells (sticky) ── */}
          {extendedColumns.map(({ key, displayName }, colIdx) => {
            const showInfo =
              !NO_INFO_COLS.has(displayName) &&
              Boolean(COLUMN_DESCRIPTIONS[displayName]);
            const headerContent = (
              <div className="flex items-center whitespace-nowrap">
                <Typography
                  variant="paragraph-m-semibold"
                  className="text-contentSecondary">
                  {truncate(displayName)}
                </Typography>
                {showInfo && (
                  <div className="-ml-1.5">
                    {/* Desktop: hover tooltip */}
                    <div className="hidden md:block">
                      <TooltipTrigger delay={200} closeDelay={300}>
                        <Button
                          variant="transparent"
                          size="small"
                          leftIcon="info-circle"
                        />
                        <Tooltip>{COLUMN_DESCRIPTIONS[displayName]}</Tooltip>
                      </TooltipTrigger>
                    </div>
                    {/* Mobile: tap popover */}
                    <div className="md:hidden">
                      <DialogTrigger>
                        <Button
                          variant="transparent"
                          size="small"
                          leftIcon="info-circle"
                        />
                        <Popover showArrow>
                          <div className="max-w-xs p-3">
                            <Typography
                              variant="paragraph-m-regular"
                              className="text-contentPrimary">
                              {COLUMN_DESCRIPTIONS[displayName]}
                            </Typography>
                          </div>
                        </Popover>
                      </DialogTrigger>
                    </div>
                  </div>
                )}
              </div>
            );

            if (colIdx === 0) {
              return (
                <div
                  key={`h-${key}`}
                  className="sticky left-0 top-0 z-20 flex items-center border-b border-r border-borderPrimary bg-backgroundSecondary px-4 py-3">
                  {headerContent}
                </div>
              );
            }
            if (key === MOLECULE_COL_KEY) {
              return (
                <div
                  key={`h-${key}`}
                  className="top-0 z-30 flex items-center border-b border-r border-borderPrimary bg-backgroundSecondary px-4 py-3 md:sticky"
                  style={{ left: moleculeLeftOffset }}>
                  {headerContent}
                </div>
              );
            }
            return (
              <div
                key={`h-${key}`}
                className="sticky top-0 z-10 flex items-center border-b border-borderPrimary bg-backgroundSecondary px-4 py-3">
                {headerContent}
              </div>
            );
          })}

          {/* ── Data cells ── */}
          {rows.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center gap-2 py-12"
              style={{ gridColumn: "1 / -1" }}>
              <DenovoIcon
                icon="search"
                width={36}
                height={36}
                className="text-contentTertiary"
              />
              <Typography
                variant="paragraph-l-medium"
                className="text-contentSecondary">
                No results found
              </Typography>
            </div>
          ) : (
            rows.flatMap((row, rowIdx) => {
              const rowId =
                typeof row[BSAD_ID_COL_KEY] === "string"
                  ? (row[BSAD_ID_COL_KEY] as string)
                  : null;
              const isSelected = rowId !== null && selectedIds.includes(rowId);
              const isHighlighted = hoveredRow === rowIdx || isSelected;
              const handleRowClick = () => onRowClick(row);
              const handleRowKeyDown = (e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onRowClick(row);
                }
              };

              return extendedColumns.map(({ key, displayName }, colIdx) => {
                const borderClass =
                  rowIdx < rows.length - 1
                    ? "border-b border-borderPrimary"
                    : "";
                const hoverBg = isHighlighted
                  ? "bg-backgroundSecondary"
                  : "bg-backgroundPrimary";

                if (colIdx === 0) {
                  // The single accessible, keyboard-operable selection toggle
                  // for this row — the other cells below are mouse-only
                  // convenience click targets for the same action.
                  return (
                    <div
                      key={`${rowIdx}-${key}`}
                      // oxlint-disable-next-line prefer-tag-over-role -- needs to render CellContent as rich clickable children, which a bare <input> can't
                      role="checkbox"
                      aria-checked={isSelected}
                      aria-label={rowId ? `Select ${rowId}` : "Select row"}
                      tabIndex={0}
                      className={`sticky left-0 z-10 flex cursor-pointer items-center border-r border-borderPrimary px-4 py-3 outline-none transition-colors data-focus-visible:outline-2 data-focus-visible:outline-offset-[-2px] data-focus-visible:outline-borderFocus ${borderClass} ${hoverBg}`}
                      onMouseEnter={() => setHoveredRow(rowIdx)}
                      onMouseLeave={() => setHoveredRow(null)}
                      onClick={handleRowClick}
                      onKeyDown={handleRowKeyDown}>
                      <CellContent colKey={key} value={row[key] ?? null} />
                    </div>
                  );
                }

                if (key === MOLECULE_COL_KEY) {
                  const smiles =
                    typeof row["SMILES"] === "string" ? row["SMILES"] : null;
                  const bsadId =
                    row[BSAD_ID_COL_KEY] != null
                      ? String(row[BSAD_ID_COL_KEY])
                      : null;
                  return (
                    // oxlint-disable-next-line no-static-element-interactions -- mouse-only convenience click target; the accessible toggle is the row's first cell
                    <div
                      key={`${rowIdx}-${MOLECULE_COL_KEY}`}
                      className={`z-10 flex cursor-pointer items-center justify-center border-r border-borderPrimary px-2 py-2 transition-colors md:sticky ${borderClass} ${hoverBg}`}
                      style={{ left: moleculeLeftOffset }}
                      onMouseEnter={() => setHoveredRow(rowIdx)}
                      onMouseLeave={() => setHoveredRow(null)}
                      onClick={handleRowClick}>
                      <SmilesMolecule smiles={smiles} id={bsadId} />
                    </div>
                  );
                }

                const hoverClass = isHighlighted
                  ? "bg-backgroundSecondary"
                  : "";
                const centerClass = LEFT_ALIGN_COLS.has(displayName)
                  ? ""
                  : "justify-center";
                return (
                  // oxlint-disable-next-line no-static-element-interactions -- mouse-only convenience click target; the accessible toggle is the row's first cell
                  <div
                    key={`${rowIdx}-${key}`}
                    className={`flex cursor-pointer items-center px-4 py-3 transition-colors ${borderClass} ${hoverClass} ${centerClass}`}
                    onMouseEnter={() => setHoveredRow(rowIdx)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={handleRowClick}>
                    <CellContent colKey={key} value={row[key] ?? null} />
                  </div>
                );
              });
            })
          )}
        </div>
      </div>
    </div>
  );
}
